import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import PieChartComponent from "../Graphs/PieChartComponent";

// Utility function to generate consistent colors
const generateColors = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = (i * 137.5) % 360;
    const saturation = 70 + Math.round((i % 3) * 10);
    const lightness = 80 - Math.round((i % 2) * 10);
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }
  return colors;
};

const UserReview = ({ analyticsData, themeBg }) => {
  const deviceData = analyticsData?.sessions?.byDevice || [];
  
  // Generate consistent colors
  const colors = useMemo(() => generateColors(deviceData.length), [deviceData.length]);
  
  // Calculate total sessions for device percentage
  const totalSessions = deviceData.reduce((sum, item) => sum + (parseInt(item.sessions) || 0), 0);
  
  // Calculate user type percentages
  const newUsers = parseInt(analyticsData?.sessions?.newUsers) || 0;
  const returningUsers = parseInt(analyticsData?.sessions?.returningUsers) || 0;
  const totalUserSessions = newUsers + returningUsers;
  
  const newUsersPercentage = totalUserSessions > 0 
    ? Math.round((newUsers / totalUserSessions) * 100) 
    : 0;
    
  const returningUsersPercentage = totalUserSessions > 0 
    ? Math.round((returningUsers / totalUserSessions) * 100) 
    : 0;
  
  return (
    <div
      style={{
        backgroundColor: themeBg || "#111827",
        borderRadius: "12px",
        padding: "20px",
        color: "#FFFFFF",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="h6"
        style={{ fontWeight: "bold", marginBottom: "10px" }}
      >
        Users Overview
      </Typography>
      
      {/* Improved label for unique users */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          marginBottom: "15px",
          backgroundColor: "rgba(255,255,255,0.02)",
          padding: "6px 8px",
          borderRadius: "4px",
          borderLeft: "3px solid rgba(255,255,255,0.2)",
        }}
      >
        <Typography
          variant="body2"
          style={{
            fontSize: "12px",
            wordBreak: "break-word",
            flex: "8",
            fontWeight: "bold",
          }}
        >
          Total Unique Users
        </Typography>
        <Typography
          variant="body2"
          style={{
            fontSize: "15px", // Slightly larger text
            textAlign: "right",
            flex: "2",
            fontWeight: "bold",
          }}
        >
          {analyticsData?.users?.unique || "N/A"}
        </Typography>
      </div>

      {/* Session distribution section header */}
      <Typography 
        variant="body2" 
        style={{ 
          fontSize: "13px", 
          opacity: 0.7, 
          marginBottom: "8px" 
        }}
      >
        Session Distribution
      </Typography>
      
      {/* New User Sessions with visual indicator */}
      <div
        style={{
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            backgroundColor: "rgba(255,255,255,0.05)",
            padding: "6px 8px",
            borderRadius: "4px 4px 0 0",
          }}
        >
          <Typography
            variant="body2"
            style={{
              fontSize: "12px",
              wordBreak: "break-word",
              flex: "8",
            }}
          >
            New User Sessions
          </Typography>
          <Typography
            variant="body2"
            style={{
              fontSize: "12px",
              textAlign: "right",
              flex: "2",
              fontWeight: "bold",
            }}
          >
            {newUsers} ({newUsersPercentage}%)
          </Typography>
        </div>
        
        {/* Progress bar for new users */}
        <div style={{ height: "4px", backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "0 0 4px 4px" }}>
          <div 
            style={{ 
              height: "100%", 
              width: `${newUsersPercentage}%`, 
              backgroundColor: "#4CAF50", 
              borderRadius: "0 0 0 4px",
            }} 
          />
        </div>
      </div>
      
      {/* Returning User Sessions with visual indicator */}
      <div
        style={{
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            backgroundColor: "rgba(255,255,255,0.05)",
            padding: "6px 8px",
            borderRadius: "4px 4px 0 0",
          }}
        >
          <Typography
            variant="body2"
            style={{
              fontSize: "12px",
              wordBreak: "break-word",
              flex: "8",
            }}
          >
            Returning User Sessions
          </Typography>
          <Typography
            variant="body2"
            style={{
              fontSize: "12px",
              textAlign: "right",
              flex: "2",
              fontWeight: "bold",
            }}
          >
            {returningUsers} ({returningUsersPercentage}%)
          </Typography>
        </div>
        
        {/* Progress bar for returning users */}
        <div style={{ height: "4px", backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "0 0 4px 4px" }}>
          <div 
            style={{ 
              height: "100%", 
              width: `${returningUsersPercentage}%`, 
              backgroundColor: "#2196F3", 
              borderRadius: "0 0 0 4px",
            }} 
          />
        </div>
      </div>
      
      {/* Device distribution header */}
      <Typography 
        variant="body2" 
        style={{ 
          fontSize: "13px", 
          opacity: 0.7, 
          marginBottom: "8px" 
        }}
      >
        Device Distribution
      </Typography>
      
      {/* Pie chart and legend */}
      <PieChartComponent
        data={deviceData}
        title="Device Sessions Distribution"
        customColors={colors}
      />
      
      {/* Legend below the pie chart */}
      <div style={{ 
        marginTop: "15px",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "10px"
      }}>
        {deviceData.map((item, index) => {
          // Calculate percentage
          const percentage = totalSessions > 0 
            ? ((item.sessions / totalSessions) * 100).toFixed(1) 
            : 0;
            
          return (
            <div 
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                marginRight: "10px"
              }}
            >
              {/* Color indicator */}
              <div style={{
                width: "12px",
                height: "12px",
                backgroundColor: colors[index],
                borderRadius: "3px"
              }} />
              
              {/* Device name with percentage */}
              <Typography variant="caption" style={{ color: "#FFFFFF" }}>
                {item.device}: {percentage}%
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserReview;
